import {
  DocketDisplayItem,
  DocketDisplayItemType,
  DocketDisplayOrder,
  DocketDisplayOrderItem,
  DocketDisplayOtherItem,
  DocketDisplayViewAllCoursesItem,
  DocketDisplayNotifierHeaderItem,
  DocketDisplayNotifierItem,
  DocketDisplayNotifierSummaryItem,
  DocketDetailsProps,
  DocketDisplayContinueItem,
  DocketDisplayContinuedItem,
  DocketDisplayBaseItem,
} from '../../common/types';
import React from 'react';
import { Text, View } from 'react-native';
import { translate } from '@oolio-group/localization';
import {
  OrderType,
  DocketStatus,
  SalesChannel,
  DocketItemStatus,
  Docket,
  NotifierDocketItems,
  OrderTypeCode,
} from '@oolio-group/domain';
import {
  getOrderTypeName,
  getDockerHeaderTitle,
  getHeaderColorByOrderType,
  getPartnerSalesChannelName,
  getCateringFulfillmentType,
} from '../../utils/docketHelper/transformDockets';
import { hasAllergens } from '../../utils/allergens';
import DocketItemStyles from './DocketItem.styles';
import DocketItemHeader from './DocketItemEntities/DocketItemHeader';
import DocketDisplayItemComponent from './DocketItemEntities/DocketDisplayItem';
import DocketItemBase from './DocketItemEntities/DocketItemBase';
import DocketDueTime from './DocketItemEntities/DocketDueTime';
import DocketItemContinue from './DocketItemEntities/DocketItemContinue';
import DocketItemReCall from './DocketItemEntities/DocketRecallButton';
import DocketVoidLabel from './DocketItemEntities/DocketVoidLabel';
import DocketTransferredLabel from './DocketItemEntities/DocketTransferredLabel';
import DocketRecalledLabel from './DocketItemEntities/DocketRecalledLabel';
import DocketItemNotes from './DocketItemEntities/DocketItemNotes';
import DocketAllergenBanner from './DocketItemEntities/DocketAllergenBanner';
import TrainingDocketHeader from './DocketItemEntities/TrainingDocketHeader';
import DocketOtherItemsLabel from './DocketItemEntities/DocketOtherItemsLabel';
import ViewAllCoursesLabel from './DocketItemEntities/ViewAllCoursesLabel';
import DocketTitleBar from './DocketItemEntities/DocketTitleBar';
import { selectedDocketVar, bulkSelectVar } from '../../state/apolloVars';
import CourseCallHeader from '../CourseCallHeader';
import { useSession } from '../../hooks/useSession';

export interface DocketItemProps {
  item?: DocketDisplayItem;
  status: DocketStatus;
  onPressDocketHeader?: (
    docketId: string,
    notifierItems: NotifierDocketItems[] | undefined,
  ) => Promise<Docket>;
  onPressDocketItem?: (
    docketItemId: string,
    itemStatus: DocketItemStatus,
  ) => Promise<void>;
  orderTypes: OrderType[];
  salesChannels?: SalesChannel[];
  onToggleDocketDetails: (a: DocketDetailsProps) => void;
  enablePrint?: boolean;
  printKitchenDocket?: (
    docketId: string,
    status: DocketStatus,
    notifiers?: NotifierDocketItems[],
  ) => void;
  onDocketCountClick?: (orderId: string) => void;
  isSelected?: boolean;
}

const DocketItem: React.FC<DocketItemProps> = ({
  item,
  status,
  onPressDocketHeader,
  onPressDocketItem,
  orderTypes,
  salesChannels = [],
  onToggleDocketDetails,
  enablePrint,
  printKitchenDocket,
  onDocketCountClick,
  isSelected = false,
}) => {
  const { session } = useSession();
  const styles = DocketItemStyles();
  const isCourseCallEnabled =
    session?.kitchenDisplay?.isCourseCallEnabled || false;
  const showStaffName = session?.kitchenDisplay?.showStaffName || false;
  const docketItems = item as DocketDisplayOtherItem;

  if (!item) return null;

  switch (item.type) {
    case DocketDisplayItemType.DOCKET_TITLE_BAR:
      return (
        <DocketTitleBar
          tags={item.tags}
          rushedAt={item.rushedAt}
          docketCount={item.docketCount}
          onDocketCountClick={onDocketCountClick}
          orderId={item.orderId}
          isSelected={isSelected}
          seatedGuests={item.seatedGuests}
        />
      );
    case DocketDisplayItemType.TRAINING_DOCKET:
      return <TrainingDocketHeader headerText={item.name} />;
    case DocketDisplayItemType.DOCKET_HEADER:
      const docket = item as unknown as DocketDisplayOrder;
      const docketName = getDockerHeaderTitle({
        orderTypes: orderTypes,
        orderTypeId: docket.orderType,
        tableName: docket.table,
        sectionName: docket.section,
        customerName: docket.customer,
        source: docket.source,
        orderNumber: docket.orderNumber,
      });
      //TODO to get ordertype name in kds event
      const baseOrderTypeName = getOrderTypeName(docket.orderType, orderTypes);
      const orderTypeCode = orderTypes.find(
        ot => ot.id === docket.orderType,
      )?.code;
      const cateringFulfillmentType = getCateringFulfillmentType(
        orderTypeCode,
        docket.fulfilmentDetails,
      );
      const orderTypeName = baseOrderTypeName + cateringFulfillmentType;
      const orderTypeShortName =
        (orderTypeCode || baseOrderTypeName) + cateringFulfillmentType;
      const salesChannelName = getPartnerSalesChannelName(
        docket.salesChannel,
        salesChannels,
      );
      const docketHeaderColor = getHeaderColorByOrderType(
        docket.orderType,
        orderTypes,
      );
      const handleOnPressDocketHeader = async () => {
        if (onPressDocketHeader) {
          await onPressDocketHeader(docket.id, docket.notifierItems);
        }
      };

      const onSelectDocket = async () => {
        if (bulkSelectVar()) {
          const currentSelection = selectedDocketVar() as Record<
            string,
            DocketDisplayOrder
          >;

          if (currentSelection[docket.id]) {
            const { [docket.id]: _, ...rest } = currentSelection;
            selectedDocketVar(rest);
          } else {
            selectedDocketVar({ ...currentSelection, [docket.id]: docket });
          }
        } else {
          selectedDocketVar({ [docket.id]: docket });
        }
      };
      return (
        <DocketItemHeader
          docketId={docket.id}
          onPressDocketHeader={handleOnPressDocketHeader}
          orderTypeName={orderTypeName}
          orderTypeShortName={orderTypeShortName}
          staffName={docket.staffName}
          showStaffName={showStaffName}
          salesChannelName={salesChannelName}
          orderNumber={docket.orderNumber}
          docketName={docketName}
          createdTime={docket.time}
          status={status}
          docketHeaderColor={docketHeaderColor}
          docketStatus={docket.status}
          isTraining={docket.isTraining}
          notifierItems={docket.notifierItems}
          onSelectDocket={onSelectDocket}
          enablePrint={enablePrint}
          printKitchenDocket={printKitchenDocket}
        />
      );
    case DocketDisplayItemType.DUE_AT:
      return <DocketDueTime name={item.name} status={status} />;
    case DocketDisplayItemType.ITEM:
      const docketItem = item as DocketDisplayOrderItem;
      const handleOnPressDocketItem = async () => {
        if (onPressDocketItem) {
          if (docketItem?.consolidatedOrderItemIds) {
            await Promise.all(
              docketItem.consolidatedOrderItemIds.map(id =>
                onPressDocketItem(id, docketItem.itemStatus),
              ),
            );
          } else {
            await onPressDocketItem(
              docketItem?.orderItemId,
              docketItem.itemStatus,
            );
          }
        }
      };
      return (
        <DocketDisplayItemComponent
          id={docketItem.id}
          quantity={docketItem.quantity}
          name={docketItem.name}
          note={docketItem.note}
          comboItems={docketItem.comboItems}
          modifiers={docketItem.modifiers}
          variant={docketItem.variant}
          itemStatus={docketItem.itemStatus}
          status={status}
          onPressDocketItem={handleOnPressDocketItem}
          seatName={docketItem?.seatName}
          allergens={docketItem.allergens}
          allergenSeatName={docketItem.allergenSeatName}
          course={docketItem.course}
          isCourseCallEnabled={isCourseCallEnabled}
        />
      );
    case DocketDisplayItemType.COURSE:
      return isCourseCallEnabled &&
        (item as DocketDisplayBaseItem).orderTypeCode ===
          OrderTypeCode.DINE_IN ? (
        <CourseCallHeader
          courseName={item.name}
          courseCallInfo={item.courseCallInfo}
          areAllItemsBumped={item.areAllItemsBumped}
          isCompleted={status === DocketStatus.COMPLETED}
        />
      ) : (
        <DocketItemBase
          name={item.name}
          primaryGrouping={item.primaryGrouping}
          displayPreference={item.displayPreference}
        />
      );
    case DocketDisplayItemType.SEAT_NUMBER:
    case DocketDisplayItemType.CATEGORY:
      return (
        <DocketItemBase
          name={item.name}
          primaryGrouping={item.primaryGrouping}
          displayPreference={item.displayPreference}
        />
      );
    case DocketDisplayItemType.ORDER_NOTE:
      return <DocketItemNotes name={item.name} />;
    case DocketDisplayItemType.ALLERGEN_BANNER:
      return <DocketAllergenBanner name={item.name} />;
    case DocketDisplayItemType.VOID:
      return <DocketVoidLabel />;
    case DocketDisplayItemType.TRANSFERRED_TO_LABEL:
      return <DocketTransferredLabel tableId={item.name} />;
    case DocketDisplayItemType.TRANSFERRED_FROM_LABEL:
      return (
        <DocketTransferredLabel
          tableId={item.name}
          transferLabelType={DocketDisplayItemType.TRANSFERRED_FROM_LABEL}
        />
      );
    case DocketDisplayItemType.OTHER_ITEMS:
      return (
        <DocketOtherItemsLabel
          docketItem={docketItems}
          onToggleDocketDetails={onToggleDocketDetails}
        />
      );
    case DocketDisplayItemType.VIEW_ALL_COURSES:
      return (
        <ViewAllCoursesLabel
          docketItem={item as DocketDisplayViewAllCoursesItem}
          onToggleDocketDetails={onToggleDocketDetails}
        />
      );
    case DocketDisplayItemType.NOTIFIER_HEADER:
      return (
        <DocketOtherItemsLabel
          onToggleDocketDetails={onToggleDocketDetails}
          notifierHeader={item as DocketDisplayNotifierHeaderItem}
        />
      );
    case DocketDisplayItemType.NOTIFIER_PROFILE:
      return (
        <View testID="notifier-profile" style={styles.notifierProfile}>
          <Text style={styles.secondaryTextStyle}>{item.name}</Text>
        </View>
      );
    case DocketDisplayItemType.NOTIFIER_ITEM:
      const notifierItem = (item as DocketDisplayNotifierItem).item;
      return (
        <DocketDisplayItemComponent
          isOtherItem
          id={notifierItem.id}
          quantity={notifierItem.quantity || 1}
          name={item.name}
          note={notifierItem.notes}
          modifiers={notifierItem.modifiers?.map(modifier => ({
            name: modifier.name || '',
          }))}
          allergens={
            hasAllergens(notifierItem.allergens)
              ? notifierItem.allergens
              : undefined
          }
          allergenSeatName={
            notifierItem.seatNumber ? `S${notifierItem.seatNumber}` : undefined
          }
          itemStatus={notifierItem.itemStatus}
          status={notifierItem.status}
          onPressDocketItem={() => Promise.resolve()}
        />
      );
    case DocketDisplayItemType.NOTIFIER_SUMMARY:
      const notifierSummary = item as DocketDisplayNotifierSummaryItem;
      const allBumped =
        notifierSummary.bumpedCount >= notifierSummary.itemCount;
      return (
        <View testID="notifier-summary" style={styles.otherItemsContainer}>
          <Text
            style={styles.otherItemsText}
          >{`${notifierSummary.name} (${notifierSummary.quantity})`}</Text>
          <Text
            style={[
              styles.notifierSummaryStatus,
              allBumped && styles.notifierSummaryStatusDone,
            ]}
          >
            {allBumped
              ? translate('kitchenDisplay.notifierBumped')
              : notifierSummary.bumpedCount === 0
              ? translate('kitchenDisplay.notifierPending')
              : translate('kitchenDisplay.notifierProgress', {
                  bumped: notifierSummary.bumpedCount,
                  total: notifierSummary.itemCount,
                })}
          </Text>
        </View>
      );
    case DocketDisplayItemType.RECALLED_TAG:
      return <DocketRecalledLabel />;
    case DocketDisplayItemType.CONTINUED:
      const docketContinued = item as DocketDisplayContinuedItem;
      return (
        <DocketItemContinue
          docketId={docketContinued.docketId}
          text={item.name}
          subtext={item?.position?.pageIndicator || ''}
          orderNumber={item?.orderNumber}
          status={docketContinued.status}
        />
      );
    case DocketDisplayItemType.CONTINUE:
      const docketContinue = item as DocketDisplayContinueItem;
      return (
        <DocketItemContinue
          docketId={docketContinue.docketId}
          text={item.name}
          subtext={item?.position?.pageIndicator || ''}
          status={docketContinue.status}
        />
      );
    case DocketDisplayItemType.RECALL_BUTTON:
      const handleOnPressDocketRecall = () => {
        const docket = item as unknown as DocketDisplayOrder;
        if (onPressDocketHeader) {
          onPressDocketHeader(docket.id, docket.notifierItems);
        }
      };
      return <DocketItemReCall onPressRecall={handleOnPressDocketRecall} />;
    default:
      return null;
  }
};

export default DocketItem;
